import { createPortal } from 'react-dom';

import type { ReactElement, ReactNode } from 'react';

export interface ModalPortalProps {
  /** The overlay subtree (scrim + dialog box) to lift into `document.body`. */
  children: ReactNode;
}

/**
 * Renders modal/overlay content into `document.body` via a React portal.
 *
 * The app's hand-rolled dialogs use a `fixed inset-0` scrim rendered inline in
 * the page tree. When such an overlay sits as a non-first child of a `space-y-*`
 * (or any margin-applying) container, Tailwind's `.space-y-* > * ~ *` rule slaps
 * a `margin-top` on it — and a margin on a `position: fixed` box offsets it,
 * leaving a visible gap at the top of the scrim. Portaling the overlay to
 * `document.body` lifts it out of that styled subtree so no sibling margin can
 * ever apply, while React context (theme, i18n, Apollo) still flows through.
 *
 * `document.body` inherits `<html dir>` + the theme class, so RTL and dark mode
 * work unchanged through the portal. apps/web is a client-only SPA, so
 * `document.body` always exists at render.
 */
export function ModalPortal({ children }: ModalPortalProps): ReactElement {
  return createPortal(children, document.body);
}
